import Api from '../api';
import type { BidderFileResponse } from './ai_management_files';

export type ProposalStatusResponse = 'nao_iniciada' | 'rascunho' | 'revisada' | 'gerada';
export type ProposalStepResponse = 'regras' | 'itens' | 'empresa' | 'declaracoes' | 'revisao';
export type ProposalAnalysisStatusResponse = 'processing' | 'partial' | 'done' | 'error';
// A V1 só atende bens; servico/obra são bloqueados na tela. Só confiável com a análise terminada
// (status done/partial).
export type ProposalNaturezaResponse = 'bens' | 'servico' | 'obra' | 'indefinida';

export type RuleStatusResponse = 'a_validar' | 'valida' | 'nao_se_aplica';
export type RuleOriginResponse = 'edital' | 'modelo' | 'resumo' | 'ajustada' | 'cliente';
export type RuleActionResponse =
  | 'impugnar'
  | 'esclarecer'
  | 'assumir_risco'
  | 'resolvida_no_planejamento';

export interface LeituraResponse {
  exige?: boolean | null;
  valor?: string | null;
  trecho?: string | null;
  arquivo?: string | null;
  pagina?: number | null;
  clausula?: string | null;
}

// confirmada_* e autor só existem em rules já gravadas; controversa/motivo/leituras/alerta são do back
// (o front não os envia de volta). Campos extras são tolerados.
export interface RuleResponse {
  regra: string;
  origem?: RuleOriginResponse | null;
  status?: RuleStatusResponse | null;
  exige?: boolean | null;
  valor?: string | null;
  trecho?: string | null;
  arquivo?: string | null;
  pagina?: number | null;
  clausula?: string | null;
  onde_conferir?: string | null;
  motivo_nao_se_aplica?: string | null;
  confirmada_em?: string | null;
  confirmada_por_id?: number | null;
  confirmada_por_nome?: string | null;
  controversa?: boolean | null;
  motivo?: string | null;
  leituras?: LeituraResponse[] | null;
  // Conflito de prazos que só o resumo do edital aponta: aviso, sem tornar a regra controversa.
  alerta?: string | null;
  acao?: RuleActionResponse | null;
  leitura_escolhida?: number | null;
  [key: string]: unknown;
}

// Só o que o back aceita do front; acao e leitura_escolhida só nas regras controversas.
export interface RulePatchPayload {
  regra: string;
  origem?: RuleOriginResponse | null;
  status: RuleStatusResponse;
  exige?: boolean | null;
  valor?: string | null;
  trecho?: string | null;
  arquivo?: string | null;
  pagina?: number | null;
  clausula?: string | null;
  motivo_nao_se_aplica?: string | null;
  // origem "ajustada" exige valor_extraido; "onde_conferir" é obrigatório na regra do cliente.
  valor_extraido?: string | null;
  onde_conferir?: string | null;
  // Só nas regras controversas.
  acao?: RuleActionResponse | null;
  leitura_escolhida?: number | null;
}

export interface ProposalAnalysisResponse {
  status: ProposalAnalysisStatusResponse;
  modelo?: Record<string, unknown> | null;
  regras?: RuleResponse[] | null;
  avisos?: string[] | null;
  motivo?: string | null;
  natureza?: ProposalNaturezaResponse | null;
}

export interface ProposalResponse {
  status: ProposalStatusResponse;
  etapa_atual: ProposalStepResponse;
  analysis: ProposalAnalysisResponse | null;
  rules: RuleResponse[];
  rules_confirmed: boolean;
  rules_outdated: boolean;
  analyzed_at: string | null;
  questionamentos: Record<string, unknown>[];
  pendencias?: Partial<Record<ProposalStepResponse, number>>;
  // Gravado ao salvar a empresa; null até lá.
  empresa_cnpj?: string | null;
  generated_at?: string | null;
  generated_files?: BidderFileResponse[];
  generated_by_name?: string | null;
  updated_at: string | null;
}

const basePath = (biddingId: number) => `/gestao_ia/licitacoes/${biddingId}/proposta`;

export const getProposal = (biddingId: number) => Api.get<ProposalResponse>(basePath(biddingId));

export const analyzeProposal = (biddingId: number) =>
  Api.post<ProposalResponse>(`${basePath(biddingId)}/analisar`);

export const updateProposalStep = (biddingId: number, etapa: ProposalStepResponse) =>
  Api.patch<ProposalResponse>(`${basePath(biddingId)}/etapa`, { etapa });

// O back sempre recebe a lista completa ("Confirmar todas" é só do front).
// O front sobe cada arquivo em "Arquivos gerados pelo licitante" e só depois registra a geração.
export const registerGeneratedProposal = (biddingId: number, fileIds: string[]) =>
  Api.post<ProposalResponse>(`${basePath(biddingId)}/gerada`, { file_ids: fileIds });

export const updateProposalRules = (biddingId: number, regras: RulePatchPayload[]) =>
  Api.patch<ProposalResponse>(`${basePath(biddingId)}/regras`, { regras });

export type ProposalItemColumnType = 'texto' | 'numero' | 'moeda' | 'data';

export interface ProposalItemColumnResponse {
  id: string;
  chave: string | null;
  nome: string;
  tipo: ProposalItemColumnType;
  origem: 'padrao' | 'cliente';
  exigida_pelo_edital: boolean;
  excluida: boolean;
  ordem: number;
}

export interface ProposalItemResponse {
  numero_item: number | null;
  numero_lote: number | null;
  descricao: string;
  unidade: string | null;
  quantidade: number | string | null;
  valor_unitario: number | null;
  valor_total: number | null;
  valor_estimado: number | null;
  selecionado: boolean;
  marca: string | null;
  modelo: string | null;
  fabricante: string | null;
  preco_unitario: number | null;
  origem: 'edital' | 'cliente';
  valores: Record<string, unknown>;
}

export interface ProposalItemsResumo {
  total_itens: number;
  itens_selecionados: number;
  total_lotes: number;
  lotes_selecionados: number;
  total_proposta: number;
}

export interface ProposalLoteResponse {
  numero_lote: number | null;
  total_itens: number;
  selecionado: boolean;
  subtotal: number;
}

export interface ProposalItemsResponse {
  itens: ProposalItemResponse[];
  paginacao: { page: number; per_page: number; total: number; total_pages: number };
  resumo: ProposalItemsResumo;
  lotes: ProposalLoteResponse[];
  julgamento: 'item' | 'lote';
  casas_decimais: number;
  colunas: ProposalItemColumnResponse[];
}

export interface ProposalItemsQuery {
  page?: number;
  per_page?: number;
  q?: string;
  lote?: number | string;
  selecionados?: boolean;
  // Com os dois, o back devolve a página onde o item está.
  ir_para_lote?: number | string;
  ir_para_item?: number | string;
}

export interface ProposalItemChange {
  numero_lote: number | null;
  numero_item: number | null;
  selecionado?: boolean;
  marca?: string | null;
  modelo?: string | null;
  fabricante?: string | null;
  preco_unitario?: number | null;
  valores?: Record<string, unknown>;
}

export const getProposalItems = (biddingId: number, query: ProposalItemsQuery = {}) =>
  Api.get<ProposalItemsResponse>(`${basePath(biddingId)}/itens`, { params: query });

export const updateProposalItems = (biddingId: number, itens: ProposalItemChange[]) =>
  Api.patch<ProposalItemsResponse>(`${basePath(biddingId)}/itens`, { itens });

export const selectAllProposalItems = (
  biddingId: number,
  selecionado: boolean,
  lote?: number | null,
) =>
  Api.patch<ProposalItemsResponse>(`${basePath(biddingId)}/itens/selecionar_todos`, {
    selecionado,
    lote,
  });

export const createProposalColumn = (
  biddingId: number,
  nome: string,
  tipo: ProposalItemColumnType,
) => Api.post<ProposalItemsResponse>(`${basePath(biddingId)}/colunas`, { nome, tipo });

export interface ProposalColumnPatch {
  nome?: string;
  ordem?: number;
  excluida?: boolean;
}

export const updateProposalColumn = (biddingId: number, id: string, patch: ProposalColumnPatch) =>
  Api.patch<ProposalItemsResponse>(`${basePath(biddingId)}/colunas/${id}`, patch);

export interface ProposalItemLine {
  numero_lote?: number | null;
  descricao: string;
  unidade: string;
  quantidade: number | string;
}

export const createProposalItemLine = (biddingId: number, linha: ProposalItemLine) =>
  Api.post<ProposalItemsResponse>(`${basePath(biddingId)}/itens`, linha);

export const deleteProposalItemLine = (
  biddingId: number,
  numeroItem: number,
  numeroLote?: number | null,
) =>
  Api.delete<ProposalItemsResponse>(`${basePath(biddingId)}/itens/${numeroItem}`, {
    params: { numero_lote: numeroLote },
  });

export interface ProposalSpreadsheetIgnoredLine {
  linha: number;
  motivo: string;
}

export interface ProposalSpreadsheetImportResponse {
  aplicadas: number;
  ignoradas: ProposalSpreadsheetIgnoredLine[];
}

// Este endpoint usa a chave "erro" (não "error", como o resto da API).
export type ProposalSpreadsheetErrorCode = 'planilha_incompativel' | 'formato_invalido';

export interface ProposalSpreadsheetErrorResponse {
  erro: ProposalSpreadsheetErrorCode;
}

export const downloadProposalSpreadsheet = (biddingId: number) =>
  Api.get<Blob>(`${basePath(biddingId)}/planilha`, { responseType: 'blob' });

export const importProposalSpreadsheet = (biddingId: number, file: File) => {
  const formData = new FormData();
  // O back lê params[:arquivo] (ou :file), não :planilha.
  formData.append('arquivo', file, file.name);
  return Api.post<ProposalSpreadsheetImportResponse | ProposalSpreadsheetErrorResponse>(
    `${basePath(biddingId)}/planilha`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

// 409 quando não há cópia para restaurar.
export const undoProposalSpreadsheet = (biddingId: number) =>
  Api.post<ProposalItemsResponse>(`${basePath(biddingId)}/planilha/desfazer`);

export interface ProposalCompetitivePriceEntry {
  valor: number;
  data: string;
  licitacao: string;
  quantidade: number;
}

export interface ProposalCompetitivePriceClientEntry {
  valor: number;
  data: string;
  licitacao: string;
  orgao: string;
}

export interface ProposalCompetitivePriceFaixa {
  competitivo_ate: number;
  intermediario_ate: number;
}

// Metadado do casamento com o histórico: não é exibido como "preço indicado" nem usado para
// sugerir nada.
export interface ProposalCompetitivePriceCasamento {
  descricao_casada: string;
  confianca: number;
}

export interface ProposalCompetitivePrice {
  orgao: ProposalCompetitivePriceEntry | null;
  cliente: ProposalCompetitivePriceClientEntry | null;
  faixa: ProposalCompetitivePriceFaixa | null;
  casamento: ProposalCompetitivePriceCasamento;
}

// numero_lote é 0 quando o item não tem lote.
export const getProposalCompetitivePrice = (
  biddingId: number,
  numeroLote: number | null,
  numeroItem: number | null,
) =>
  Api.get<ProposalCompetitivePrice>(
    `${basePath(biddingId)}/itens/${numeroLote ?? 0}/${numeroItem}/preco_competitivo`,
  );
